import React, { memo, useRef } from "react"

import ContractQuote from "../ContractQuote"
import { useTranslation } from "react-i18next"

const ContractCard = memo(function SectionCard({
  section,
  index,
  currentLang,
  active,
  status,
  onSetStatus,
  onEdit,
  onClick,
  setRef,
  withoutEdit = false,
}) {
  const { t } = useTranslation()
  const quoteBoxRef = useRef(null)
  const touchYRef = useRef(0)

  const refProp =
    typeof setRef === "function" || (setRef && typeof setRef === "object")
      ? setRef
      : undefined

  const understood = status === "understood"
  const unclear = status === "notUnderstood"
  const reviewed = understood || unclear

  const blockAll = (e) => {
    e.stopPropagation?.()
    if (e.nativeEvent) {
      e.nativeEvent.stopImmediatePropagation?.()
    }
    return false
  }

  const onWheelInside = (e) => {
    const el = quoteBoxRef.current
    if (!el) return
    const dy = e.deltaY || 0
    if (dy !== 0) el.scrollTop += dy
    blockAll(e)
  }

  const onTouchStartInside = (e) => {
    touchYRef.current = e.touches[0].clientY
    blockAll(e)
  }

  const onTouchMoveInside = (e) => {
    const el = quoteBoxRef.current
    if (!el) return
    const y = e.touches[0].clientY
    const dy = touchYRef.current - y
    touchYRef.current = y
    el.scrollTop += dy
    blockAll(e)
  }

  return (
    <div ref={refProp} className="flex items-stretch w-full mb-7">
      <div className="w-2 flex-shrink-0" />

      <div className="flex-1 ml-4 min-w-0 max-w-full">
        <div
          className={`bg-white rounded-2xl p-3 transition-all duration-200 ease-out cursor-pointer w-full transform-gpu will-change-transform
            ${
              active
                ? "opacity-100 scale-100 -translate-y-3 relative z-20"
                : "opacity-30 scale-[0.98] translate-y-0"
            }
          `}
          style={{ maxWidth: "100%", overflow: "hidden" }}
          onClick={() => onClick(index)}
        >
          <h3 className="text-lg font-bold text-dark-default mb-3 leading-6 break-words overflow-hidden">
            {section?.emoji}{" "}
            {section?.title?.[currentLang.toUpperCase()] ||
              t("contract.unknownSection")}
          </h3>

          <div
            ref={quoteBoxRef}
            className="relative max-h-[140px] overflow-y-auto overflow-x-hidden pr-1 overscroll-contain"
            style={{ touchAction: "auto", WebkitOverflowScrolling: "touch" }}
            onWheel={onWheelInside}
            onWheelCapture={onWheelInside}
            onTouchStart={onTouchStartInside}
            onTouchStartCapture={onTouchStartInside}
            onTouchMove={onTouchMoveInside}
            onTouchMoveCapture={onTouchMoveInside}
          >
            <div className="break-words whitespace-pre-wrap max-w-full">
              <ContractQuote
                text={
                  section.quoteTranslated?.[currentLang.toUpperCase()] ||
                  t("contract.noQuoteProvided")
                }
              />
            </div>
          </div>

          <p className="text-sm text-dark-default font-bold leading-5 mt-3 break-words overflow-hidden">
            {section?.explanation?.[currentLang.toUpperCase()] ||
              t("contract.noExplanationProvided")}
          </p>

          {!reviewed && active && !withoutEdit && (
            <div className="flex flex-col gap-2 mt-4">
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onSetStatus(index, "understood")
                }}
                className="bg-success-default text-light-default font-semibold text-sm py-2 px-4 rounded-lg transition-colors hover:bg-success-t-lighten-1 flex flex-col items-center"
              >
                <span>{`${t("contract.understood")} ✓`}</span>
                <span>{t("contract.markToReview")}</span>
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onSetStatus(index, "notUnderstood")
                }}
                className="bg-black-t-lighten-6 text-dark-default font-semibold text-sm py-2 px-4 rounded-lg transition-colors hover:bg-black-t-lighten-5"
              >
                {t("contract.notUnderstood")}
              </button>
            </div>
          )}

          {reviewed && active && (
            <div className="flex flex-col gap-2 mt-4">
              <div className="py-2 rounded-lg items-center w-full justify-center flex">
                <span
                  className={`font-semibold text-sm ${
                    understood ? "text-success-default" : "text-danger-default"
                  }`}
                >
                  {understood
                    ? `✓ ${t("contract.understood")}`
                    : `✗ ${t("contract.notUnderstood")}`}
                </span>
              </div>
              {!withoutEdit && (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    onEdit(index)
                  }}
                  className="bg-black-t-lighten-6 text-dark-default font-semibold text-sm py-2 px-4 rounded-lg transition-colors hover:bg-black-t-lighten-5"
                >
                  {t("contract.edit")}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
})

export default ContractCard
